import { ReactNode } from 'react';
import { cx } from '@linaria/atomic';
import { Text } from '../';
import { BackIcon } from '../../icon';
import { FilePreviewImage } from './file-preview-image.component';
import {
  boxBaseStyle,
  boxDefaultStyle,
  boxDraggingStyle,
  buttonStyle,
  containerStyle,
  errorTextStyle,
  iconStyle,
  imagePreviewContainerStyle,
  imagePreviewStyle,
  imagePreviewTextStyle,
} from './file-upload.styles';
import { AcceptedFileType, OnFileUpload, useFileUpload } from './use-file-upload';

export type FileUploadProps = {
  // This method should simply set state for your component to retrieve the file list to be submitted
  onFileUpload?: OnFileUpload;
  helperText?: ReactNode;
  acceptedFileType?: AcceptedFileType;
  // max allowed file size in bytes
  maxFileSize?: number;
  // option to upload multiple files
  multiple?: boolean;
  // keeps the previously selected files in the array
  keepPreviouslySelected?: boolean;
  // optional validation check, returns an error message
  validateUpload?: (file: File) => Promise<string>;
  onFileProcess?: (file: File) => Promise<File>;
  trackingId?: string;
  className?: string;
};

export const FileUpload = ({
  onFileUpload,
  acceptedFileType = 'any',
  helperText = 'Upload files or drag and drop them here.',
  maxFileSize = 52428800,
  multiple = false,
  keepPreviouslySelected = true,
  validateUpload,
  trackingId,
  onFileProcess,
  className,
  ...rest
}: FileUploadProps) => {
  const { validFiles, isDragging, error, inputProps, dropContainerProps } = useFileUpload({
    acceptedFileType,
    onFileUpload,
    maxFileSize,
    keepPreviouslySelected,
    validateUpload,
    onFileProcess,
  });

  return (
    <div className={cx(containerStyle, className)} data-trackingid={trackingId} {...rest}>
      <div
        id='drop-area'
        {...dropContainerProps}
        className={cx(boxBaseStyle, isDragging ? boxDraggingStyle : boxDefaultStyle)}
      >
        <BackIcon color='light' className={iconStyle} />
        {typeof helperText === 'string' ? <Text>{helperText}</Text> : helperText}
        <label htmlFor='file-drop' className={buttonStyle}>
          Choose Files
        </label>
        <input type='file' id='file-drop' multiple={multiple} hidden {...inputProps} />
      </div>
      {validFiles.length > 0 && (
        <div className={cx('image-preview-container', imagePreviewContainerStyle)}>
          {validFiles.map((file: File) => (
            <div key={file.name} className={imagePreviewStyle}>
              <FilePreviewImage file={file} />
              <Text className={imagePreviewTextStyle}>{file.name}</Text>
            </div>
          ))}
        </div>
      )}

      {!!error && <Text className={errorTextStyle}>{error}</Text>}
    </div>
  );
};
